import React, { useCallback, useEffect, useMemo, useRef, useState, useContext, createContext } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { io } from "socket.io-client";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import CloseIcon from "@mui/icons-material/Close";
import ReplyIcon from "@mui/icons-material/Reply";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  getReviews,
  submitReview,
  voteHelpful,
  replyToReview,
  deleteReview,
  moderateReview,
  uploadReviewImage,
} from "../../api/reviewsApi";
import { goToSignIn } from "../../utils/signInReturn";

// ─────────────────────────────────────────────────────────────────────────────
// Reviews — single-file polymorphic review block for the web client.
//
// Drop-in component that renders the Amazon-style review block for any
// (subjectType, subjectId) pair. Owns its own data fetching, socket
// subscription, paginate-load-more state, write-review modal, and helpful
// voting. Designed so that vendor + product pages stay simple and just
// render `<Reviews subjectType=… subjectId=… />`.
// ─────────────────────────────────────────────────────────────────────────────

// ── Toast context ────────────────────────────────────────────────────────────
const ToastCtx = createContext(null);

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const push = useCallback((message, type = "error") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <ToastStack>
        {toasts.map((t) => (
          <ToastPill key={t.id} $type={t.type}>
            <ToastIcon $type={t.type}>
              {t.type === "success" ? "✓" : t.type === "info" ? "ℹ" : "✕"}
            </ToastIcon>
            {t.message}
          </ToastPill>
        ))}
      </ToastStack>
    </ToastCtx.Provider>
  );
};

const useToast = () => useContext(ToastCtx);

const SORT_OPTIONS = [
  { value: "most_helpful", label: "Most helpful" },
  { value: "most_recent", label: "Most recent" },
  { value: "highest_rated", label: "Highest rated" },
  { value: "lowest_rated", label: "Lowest rated" },
];

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_SERVER_URL;

const Stars = ({ value = 0, size = 18, onChange, color = "#f59e0b" }) => (
  <span style={{ display: "inline-flex", alignItems: "center" }}>
    {[1, 2, 3, 4, 5].map((s) => {
      const filled = s <= Math.round(value);
      const Icon = filled ? StarIcon : StarBorderIcon;
      const interactive = typeof onChange === "function";
      return (
        <Icon
          key={s}
          onClick={interactive ? () => onChange(s) : undefined}
          style={{
            fontSize: size,
            color: filled ? color : "#cbd5e1",
            cursor: interactive ? "pointer" : "default",
          }}
        />
      );
    })}
  </span>
);

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

const Histogram = ({ histogram = {}, total = 0 }) => (
  <HistogramWrap>
    {[5, 4, 3, 2, 1].map((star) => {
      const count = Number(histogram[star] || 0);
      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
      return (
        <HistRow key={star}>
          <span style={{ width: 50, color: "#475569", fontSize: 12 }}>
            {star} star
          </span>
          <Bar>
            <BarFill style={{ width: `${pct}%` }} />
          </Bar>
          <span style={{ width: 36, textAlign: "right", color: "#475569", fontSize: 12 }}>
            {pct}%
          </span>
          <span style={{ width: 32, textAlign: "right", color: "#94a3b8", fontSize: 12 }}>
            {count}
          </span>
        </HistRow>
      );
    })}
  </HistogramWrap>
);

const SubmitModal = ({
  visible,
  onClose,
  subjectType,
  subjectId,
  orderId,
  existingReview,
  onSubmitted,
}) => {
  const toast = useToast();
  const [rating, setRating] = useState(0);
  const [reviewTitle, setReviewTitle] = useState("");
  const [body, setBody] = useState("");
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setRating(existingReview?.rating || 0);
    setReviewTitle(existingReview?.title || "");
    setBody(existingReview?.body || "");
    setImages(Array.isArray(existingReview?.images) ? existingReview.images : []);
  }, [visible, existingReview]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploads = [];
      for (const f of files.slice(0, 5 - images.length)) {
        // Sequential uploads keep the request count predictable on slow
        // networks; the unsigned preset rate-limit on Cloudinary is
        // generous enough that this is not a real bottleneck.
        // eslint-disable-next-line no-await-in-loop
        uploads.push(await uploadReviewImage(f));
      }
      setImages((prev) => [...prev, ...uploads].slice(0, 5));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[reviews] upload failed", err?.message);
      toast("Image upload failed. Please try again.", "error");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!rating) {
      toast("Please pick a star rating before submitting.", "warning");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await submitReview({
        subjectType,
        subjectId,
        rating,
        title: reviewTitle,
        body,
        images,
        orderId,
      });
      if (data?.success && data?.data) {
        onSubmitted?.(data.data);
        onClose?.();
      } else {
        toast(data?.message || "Could not submit review", "error");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message;
      toast(msg || "Could not submit review", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;
  return (
    <ModalBackdrop onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h3 style={{ margin: 0 }}>Share your review</h3>
          <button
            type="button"
            onClick={onClose}
            style={{ border: "none", background: "transparent", cursor: "pointer" }}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </ModalHeader>
        <Label>Your overall rating</Label>
        <Stars value={rating} size={32} onChange={setRating} />
        <Label>Headline (optional)</Label>
        <Input
          value={reviewTitle}
          onChange={(e) => setReviewTitle(e.target.value)}
          maxLength={120}
          placeholder="Summarise your experience"
        />
        <Label>Your review (optional)</Label>
        <TextArea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={2000}
          placeholder="What did you like? What could be better?"
        />
        <Label>Photos (optional, up to 5)</Label>
        <ThumbStrip>
          {images.map((img, idx) => (
            <ThumbWrap key={`${img.url}-${idx}`}>
              <img src={img.url} alt="" />
              <button
                type="button"
                onClick={() => setImages(images.filter((_, i) => i !== idx))}
                aria-label="Remove image"
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </button>
            </ThumbWrap>
          ))}
          {images.length < 5 && (
            <AddThumb>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFiles}
                disabled={uploading}
              />
              {uploading ? "Uploading…" : "+ Add"}
            </AddThumb>
          )}
        </ThumbStrip>
        <SubmitButton
          onClick={handleSubmit}
          disabled={submitting || !rating}
          style={{ marginTop: 14 }}
        >
          {submitting
            ? "Submitting…"
            : existingReview
              ? "Update review"
              : "Submit review"}
        </SubmitButton>
      </ModalCard>
    </ModalBackdrop>
  );
};

const ReviewItem = ({ review, currentUserId, canModerate, canReply, onChanged }) => {
  const [voting, setVoting] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [savingReply, setSavingReply] = useState(false);
  const isOwn =
    currentUserId && String(review.reviewer) === String(currentUserId);

  const handleHelpful = async () => {
    if (voting) return;
    setVoting(true);
    try {
      const { data } = await voteHelpful(review._id);
      if (data?.success && data?.data) onChanged?.(data.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[reviews] helpful failed", err?.message);
    } finally {
      setVoting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this review?")) return;
    try {
      const { data } = await deleteReview(review._id);
      if (data?.success) onChanged?.({ _id: review._id, _deleted: true });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[reviews] delete failed", err?.message);
    }
  };

  const handleHide = async () => {
    try {
      const { data } = await moderateReview(review._id, !review.isHidden);
      if (data?.success && data?.data) onChanged?.(data.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[reviews] moderate failed", err?.message);
    }
  };

  const submitReply = async () => {
    const txt = replyText.trim();
    if (!txt) return;
    setSavingReply(true);
    try {
      const { data } = await replyToReview(review._id, txt);
      if (data?.success && data?.data) {
        onChanged?.(data.data);
        setReplyOpen(false);
        setReplyText("");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[reviews] reply failed", err?.message);
    } finally {
      setSavingReply(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <Avatar>
          {(review.reviewerName || "U").trim().slice(0, 1).toUpperCase()}
        </Avatar>
        <div style={{ flex: 1 }}>
          <strong>{review.reviewerName || "Customer"}</strong>
          <MetaRow>
            <Stars value={review.rating} size={14} />
            <span style={{ color: "#64748b", fontSize: 12 }}>
              {formatDate(review.createdAt)}
            </span>
          </MetaRow>
          {review.verifiedPurchase && (
            <VerifiedBadge>
              <VerifiedIcon style={{ fontSize: 14, color: "#16a34a" }} />
              Verified Purchase
            </VerifiedBadge>
          )}
        </div>
        {(isOwn || canModerate) && (
          <SmallButton
            type="button"
            onClick={isOwn ? handleDelete : handleHide}
          >
            {isOwn ? "Delete" : review.isHidden ? "Unhide" : "Hide"}
          </SmallButton>
        )}
      </CardHeader>

      {review.title ? <h4 style={{ margin: "6px 0 0" }}>{review.title}</h4> : null}
      {review.body ? (
        <p style={{ margin: "6px 0 0", color: "#334155", lineHeight: 1.45 }}>
          {review.body}
        </p>
      ) : null}

      {Array.isArray(review.images) && review.images.length > 0 && (
        <ImagesRow>
          {review.images.map((img, idx) => (
            <img
              key={`${img.url}-${idx}`}
              src={img.url}
              alt=""
              loading="lazy"
            />
          ))}
        </ImagesRow>
      )}

      <CardFooter>
        <HelpfulBtn
          type="button"
          $active={review.viewerHasVotedHelpful}
          disabled={voting}
          onClick={handleHelpful}
        >
          <ThumbUpAltOutlinedIcon style={{ fontSize: 14 }} />
          Helpful{review.helpfulCount > 0 ? ` (${review.helpfulCount})` : ""}
        </HelpfulBtn>
        {canReply && !review.vendorReply && (
          <SmallButton type="button" onClick={() => setReplyOpen(true)}>
            <ReplyIcon style={{ fontSize: 14 }} /> Reply
          </SmallButton>
        )}
      </CardFooter>

      {review.vendorReply && (
        <ReplyBlock>
          <strong style={{ color: "#64748b", fontSize: 12 }}>
            Response from the seller
          </strong>
          <p style={{ margin: "4px 0 0", color: "#0f172a" }}>
            {review.vendorReply.body}
          </p>
          <span style={{ color: "#94a3b8", fontSize: 11 }}>
            {formatDate(review.vendorReply.repliedAt)}
          </span>
        </ReplyBlock>
      )}

      {replyOpen && (
        <div style={{ marginTop: 8 }}>
          <TextArea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a public response…"
            maxLength={1000}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
            <SmallButton type="button" onClick={() => setReplyOpen(false)}>
              Cancel
            </SmallButton>
            <SubmitButton
              type="button"
              onClick={submitReply}
              disabled={savingReply || !replyText.trim()}
              style={{ padding: "6px 12px" }}
            >
              {savingReply ? "Posting…" : "Post reply"}
            </SubmitButton>
          </div>
        </div>
      )}
    </Card>
  );
};

const ReviewsInner = ({
  subjectType,
  subjectId,
  orderId,
  canReply = false,
  canModerate = false,
  title = "Reviews",
  reserveFloatingActionSpace = false,
}) => {
  const history = useHistory();
  const currentUser = useSelector((s) => s.user?.currentUser);
  const currentUserId =
    currentUser?._id || currentUser?.id || currentUser?.result?._id || null;

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({
    averageRating: 0,
    totalReviews: 0,
    verifiedReviews: 0,
    histogram: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [sort, setSort] = useState("most_helpful");
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [emptyExpanded, setEmptyExpanded] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [signInPrompt, setSignInPrompt] = useState(false);

  const ownReview = useMemo(
    () =>
      currentUserId
        ? items.find((i) => String(i.reviewer) === String(currentUserId))
        : null,
    [items, currentUserId],
  );

  const fetchPage = useCallback(
    async ({ reset = false, nextPage } = {}) => {
      if (!subjectType || !subjectId) return;
      const target = reset ? 1 : nextPage || page;
      setLoading(true);
      try {
        const { data } = await getReviews({
          subjectType,
          subjectId,
          sort,
          page: target,
          limit: 10,
        });
        if (data?.success && data?.data) {
          setStats(
            data.data.stats || {
              averageRating: 0,
              totalReviews: 0,
              verifiedReviews: 0,
              histogram: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
            },
          );
          setHasMore(!!data.data.hasMore);
          setPage(target);
          setItems((prev) =>
            reset || target === 1 ? data.data.items : [...prev, ...data.data.items],
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[reviews] fetch failed", err?.message);
      } finally {
        setLoading(false);
        setHasLoaded(true);
      }
    },
    [subjectType, subjectId, sort, page],
  );

  useEffect(() => {
    fetchPage({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectType, subjectId, sort]);

  useEffect(() => {
    setHasLoaded(false);
    setEmptyExpanded(false);
  }, [subjectType, subjectId]);

  // Realtime: keep this socket scoped to this component instance.
  // SocketProvider isn't wired into the web client, so we bring up our own
  // connection identical in pattern to MomoOrderScreen / LandingPage.
  const socketRef = useRef(null);
  useEffect(() => {
    if (!subjectType || !subjectId || !SOCKET_URL) return undefined;
    const socket = io(SOCKET_URL, { transports: ["websocket"] });
    socketRef.current = socket;
    const join = () => socket.emit("joinReviewRoom", { subjectType, subjectId });
    socket.on("connect", join);

    const handle = (eventName) => (payload) => {
      if (!payload) return;
      if (eventName === "review:deleted") {
        setItems((prev) => prev.filter((r) => String(r._id) !== String(payload._id)));
        setStats((s) => ({
          ...s,
          totalReviews: Math.max(0, (s.totalReviews || 1) - 1),
        }));
        return;
      }
      if (eventName === "review:moderated" && payload.isHidden) {
        setItems((prev) => prev.filter((r) => String(r._id) !== String(payload._id)));
        return;
      }
      setItems((prev) => {
        const idx = prev.findIndex((r) => String(r._id) === String(payload._id));
        if (idx === -1) {
          return sort === "most_recent" || sort === "most_helpful"
            ? [payload, ...prev]
            : prev;
        }
        const next = [...prev];
        next[idx] = { ...next[idx], ...payload };
        return next;
      });
    };
    const handlers = [
      ["review:upserted", handle("review:upserted")],
      ["review:helpful", handle("review:helpful")],
      ["review:reply", handle("review:reply")],
      ["review:deleted", handle("review:deleted")],
      ["review:moderated", handle("review:moderated")],
    ];
    handlers.forEach(([ev, fn]) => socket.on(ev, fn));

    return () => {
      try {
        socket.emit("leaveReviewRoom", { subjectType, subjectId });
      } catch {
        // socket already torn down
      }
      handlers.forEach(([ev, fn]) => socket.off(ev, fn));
      socket.off("connect", join);
      socket.close();
      socketRef.current = null;
    };
  }, [subjectType, subjectId, sort]);

  const handleCardChanged = (next) => {
    if (next?._deleted) {
      setItems((prev) => prev.filter((r) => String(r._id) !== String(next._id)));
      return;
    }
    setItems((prev) => {
      const idx = prev.findIndex((r) => String(r._id) === String(next._id));
      if (idx === -1) return [next, ...prev];
      const out = [...prev];
      out[idx] = { ...out[idx], ...next };
      return out;
    });
  };

  const handleOwnSubmitted = (review) => {
    handleCardChanged(review);
    fetchPage({ reset: true });
  };

  const hasReviewData = stats.totalReviews > 0 || items.length > 0;
  const isEmpty = hasLoaded && !hasReviewData;

  if (!subjectType || !subjectId) return null;

  return (
    <Wrap>
      {isEmpty ? (
        <EmptySectionToggle
          type="button"
          onClick={() => setEmptyExpanded((expanded) => !expanded)}
          aria-expanded={emptyExpanded}
          aria-controls={`reviews-empty-${subjectType}-${subjectId}`}
          $reserveFloatingActionSpace={reserveFloatingActionSpace}
        >
          <EmptySectionLead>
            <EmptySectionIcon aria-hidden="true">
              <RateReviewOutlinedIcon style={{ fontSize: 22 }} />
            </EmptySectionIcon>
            <EmptySectionCopy>
              <strong>{title}</strong>
              <span>No reviews yet · Be the first to share an experience</span>
            </EmptySectionCopy>
          </EmptySectionLead>
          <EmptySectionAction $expanded={emptyExpanded}>
            <span>{emptyExpanded ? "Hide options" : "Review options"}</span>
            <ExpandMoreRoundedIcon aria-hidden="true" />
          </EmptySectionAction>
        </EmptySectionToggle>
      ) : (
        <h2 style={{ margin: "0 0 12px" }}>{title}</h2>
      )}

      {(!isEmpty || emptyExpanded) && (
        <ReviewContent id={isEmpty ? `reviews-empty-${subjectType}-${subjectId}` : undefined}>
      <SummaryCard>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <div style={{ minWidth: 140 }}>
            <div style={{ fontSize: 36, fontWeight: 700, color: "#0f172a" }}>
              {Number(stats.averageRating || 0).toFixed(1)}
            </div>
            <Stars value={stats.averageRating} size={18} />
            <div style={{ color: "#64748b", marginTop: 4, fontSize: 13 }}>
              {stats.totalReviews}{" "}
              {stats.totalReviews === 1 ? "review" : "reviews"}
            </div>
            {stats.verifiedReviews > 0 && (
              <VerifiedBadge style={{ marginTop: 6 }}>
                <VerifiedIcon style={{ fontSize: 14, color: "#16a34a" }} />
                {stats.verifiedReviews} verified
              </VerifiedBadge>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 260 }}>
            <Histogram histogram={stats.histogram} total={stats.totalReviews} />
          </div>
        </div>
        <WriteBtn
          type="button"
          onClick={() => {
            if (!currentUserId) {
              setSignInPrompt(true);
              return;
            }
            setSubmitOpen(true);
          }}
        >
          {ownReview ? "Edit your review" : "Write a review"}
        </WriteBtn>
        {signInPrompt && (
          <SignInBanner>
            <SignInBannerIcon><LockOutlinedIcon style={{ fontSize: 20 }} /></SignInBannerIcon>
            <SignInBannerText>
              <strong>Sign in to write a review</strong>
              <span>Share your experience and help others make better choices.</span>
            </SignInBannerText>
            <SignInBannerActions>
              <SignInBtn
                type="button"
                onClick={() => goToSignIn(history)}
              >
                Sign In
              </SignInBtn>
              <RegisterBtn
                type="button"
                onClick={() => goToSignIn(history, undefined, "/register")}
              >
                Create Account
              </RegisterBtn>
              <ClosePromptBtn type="button" onClick={() => setSignInPrompt(false)} aria-label="Dismiss">
                <CloseIcon style={{ fontSize: 16 }} />
              </ClosePromptBtn>
            </SignInBannerActions>
          </SignInBanner>
        )}
        <SortRow>
          <span style={{ color: "#475569", fontSize: 12, fontWeight: 600 }}>
            Sort by
          </span>
          {SORT_OPTIONS.map((opt) => (
            <SortPill
              key={opt.value}
              type="button"
              $active={sort === opt.value}
              onClick={() => setSort(opt.value)}
            >
              {opt.label}
            </SortPill>
          ))}
        </SortRow>
      </SummaryCard>

      {items.length === 0 && !loading ? (
        <EmptyBox>
          <strong>No reviews yet</strong>
          <span>Be the first to share what you think.</span>
        </EmptyBox>
      ) : (
        items.map((review) => (
          <ReviewItem
            key={review._id}
            review={review}
            currentUserId={currentUserId}
            canReply={canReply}
            canModerate={canModerate}
            onChanged={handleCardChanged}
          />
        ))
      )}

      {hasMore && (
        <LoadMore
          type="button"
          onClick={() => fetchPage({ nextPage: page + 1 })}
          disabled={loading}
        >
          {loading ? "Loading…" : "Load more reviews"}
        </LoadMore>
      )}
        </ReviewContent>
      )}

      <SubmitModal
        visible={submitOpen}
        onClose={() => setSubmitOpen(false)}
        subjectType={subjectType}
        subjectId={subjectId}
        orderId={orderId}
        existingReview={ownReview}
        onSubmitted={handleOwnSubmitted}
      />
    </Wrap>
  );
};

const Reviews = (props) => (
  <ToastProvider>
    <ReviewsInner {...props} />
  </ToastProvider>
);

// ── styled-components ────────────────────────────────────────────────────────
const Wrap = styled.section`
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-width: 0;
`;
const EmptySectionToggle = styled.button`
  width: 100%;
  max-width: 720px;
  align-self: flex-start;
  background: linear-gradient(135deg, #ffffff 0%, #f7fbf9 100%);
  border: 1px solid #dce8e2;
  border-radius: 16px;
  padding: 13px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: #0f172a;
  cursor: pointer;
  text-align: left;
  font: inherit;
  box-shadow: 0 8px 24px rgba(15, 70, 45, 0.07);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: #9dcbb5;
    box-shadow: 0 10px 28px rgba(15, 123, 77, 0.12);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 3px solid rgba(15, 23, 42, 0.18);
    outline-offset: 2px;
  }

  @media (max-width: 1200px) {
    max-width: calc(100% - 280px);
  }

  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
    align-self: stretch;
    align-items: center;
    box-sizing: border-box;
    padding-right: ${(p) => (p.$reserveFloatingActionSpace ? "82px" : "14px")};
  }
`;
const EmptySectionLead = styled.span`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;
const EmptySectionIcon = styled.span`
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #0f7b4d;
  background: linear-gradient(135deg, #e8f7ef 0%, #d8f0e4 100%);
  box-shadow: inset 0 0 0 1px rgba(15, 123, 77, 0.08);
`;
const EmptySectionCopy = styled.span`
  display: grid;
  gap: 2px;
  min-width: 0;

  strong {
    color: #13231b;
    font-size: 1rem;
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > span {
    color: #718078;
    font-size: 0.76rem;
    line-height: 1.35;
  }
`;
const EmptySectionAction = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 999px;
  padding: 7px 9px 7px 12px;
  color: #0f7b4d;
  background: #edf8f2;
  font-size: 0.75rem;
  font-weight: 800;

  svg {
    font-size: 19px;
    transform: rotate(${(p) => (p.$expanded ? "180deg" : "0deg")});
    transition: transform 0.2s ease;
  }

  @media (max-width: 520px) {
    padding: 7px;
    > span { display: none; }
  }

  @media (max-width: 360px) {
    margin-left: -4px;
  }
`;
const ReviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const SummaryCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const HistogramWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const HistRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const Bar = styled.div`
  flex: 1;
  height: 10px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
`;
const BarFill = styled.div`
  height: 10px;
  background: #f59e0b;
`;
const WriteBtn = styled.button`
  background: #0f172a;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 12px 16px;
  font-weight: 600;
  cursor: pointer;
  align-self: flex-start;
  &:hover { opacity: 0.9; }
`;
const SortRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;
const SortPill = styled.button`
  border: none;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  background: ${(p) => (p.$active ? "#0f172a" : "#f1f5f9")};
  color: ${(p) => (p.$active ? "#fff" : "#0f172a")};
  font-weight: ${(p) => (p.$active ? 600 : 500)};
`;
const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  background: #0f172a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
`;
const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
`;
const VerifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #16a34a;
  font-size: 11px;
  font-weight: 600;
  margin-top: 4px;
`;
const ImagesRow = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  margin-top: 6px;
  img {
    width: 96px;
    height: 96px;
    object-fit: cover;
    border-radius: 8px;
    background: #f1f5f9;
  }
`;
const CardFooter = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;
const HelpfulBtn = styled.button`
  border: none;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${(p) => (p.$active ? "#0f172a" : "#f1f5f9")};
  color: ${(p) => (p.$active ? "#fff" : "#0f172a")};
`;
const SmallButton = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;
const ReplyBlock = styled.div`
  margin-top: 8px;
  padding: 10px;
  border-radius: 10px;
  background: #f8fafc;
  border-left: 3px solid #0f172a;
`;
const EmptyBox = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 18px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #475569;
`;
const LoadMore = styled.button`
  align-self: center;
  background: transparent;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 600;
  &:disabled { opacity: 0.6; }
`;
const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15,23,42,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
`;
const ModalCard = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;
const Label = styled.label`
  margin-top: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
`;
const Input = styled.input`
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px;
  font-size: 14px;
  outline: none;
`;
const TextArea = styled.textarea`
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px;
  font-size: 14px;
  min-height: 90px;
  resize: vertical;
  outline: none;
`;
const ThumbStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;
const ThumbWrap = styled.div`
  position: relative;
  img { width: 84px; height: 84px; border-radius: 10px; object-fit: cover; }
  button {
    position: absolute;
    top: -8px;
    right: -8px;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 999px;
    padding: 0;
    width: 22px;
    height: 22px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
const AddThumb = styled.label`
  width: 84px;
  height: 84px;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  position: relative;
  input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
  }
`;
const SubmitButton = styled.button`
  background: #0f172a;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ── Toast styled-components ──────────────────────────────────────────────────
const ToastStack = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
`;

const ToastPill = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  box-shadow: 0 4px 20px rgba(0,0,0,0.18);
  animation: slideIn 0.25s ease;
  max-width: 360px;
  background: ${(p) =>
    p.$type === "success" ? "#16a34a" :
    p.$type === "info"    ? "#0369a1" :
    p.$type === "warning" ? "#b45309" :
    "#dc2626"
  };
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to   { transform: translateX(0);    opacity: 1; }
  }
`;

const ToastIcon = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
`;

// ── Sign-in prompt styled-components ─────────────────────────────────────────
const SignInBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  padding: 14px 16px;
  margin-top: 10px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SignInBannerIcon = styled.span`
  font-size: 22px;
  flex-shrink: 0;
`;

const SignInBannerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;

  strong {
    font-size: 14px;
    font-weight: 700;
    color: #1e3a5f;
  }

  span {
    font-size: 12px;
    color: #475569;
  }
`;

const SignInBannerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const SignInBtn = styled.button`
  background: #0f172a;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;

  &:hover {
    background: #1e293b;
  }
`;

const RegisterBtn = styled.button`
  background: transparent;
  color: #0f172a;
  border: 1.5px solid #94a3b8;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    border-color: #0f172a;
    background: #f1f5f9;
  }
`;

const ClosePromptBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 50%;

  &:hover {
    color: #475569;
    background: #e2e8f0;
  }
`;

export default Reviews;
